import React, { useState } from "react";
import { TwitterLogoIcon as TwitterLogo, FacebookLogoIcon as FacebookLogo, LinkedinLogoIcon as LinkedinLogo, EnvelopeSimpleIcon as EnvelopeSimple, LinkIcon as LinkIcon, CheckIcon as Check, ShareNetworkIcon as ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/nativeBridge";
import { nativeShare, haptic } from "@/lib/native";

/**
 * ShareStrip — minimal social-share row for journal posts (and any
 * other shareable content). Uses intent URLs only (no third-party
 * SDKs, no tracking pixels) so the page stays fast and private.
 *
 * Props:
 *   - url         : absolute URL to share (required)
 *   - title       : human-readable title used in tweet/post text
 *   - dek         : optional one-line summary; only used for email body
 *   - testIdPrefix: data-testid prefix (e.g. "journal-share-top")
 *
 * Why intent URLs instead of SDKs:
 *   - Facebook SDK adds ~150kb + cookie banners; the share-dialog
 *     intent URL works without any of that.
 *   - X/Twitter killed their official share button — `intent/tweet`
 *     is the only thing that still renders predictably.
 *   - LinkedIn shareArticle is also intent-only post-2024.
 */
export default function ShareStrip({ url, title, dek, testIdPrefix = "share" }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;

  const encUrl = encodeURIComponent(url);
  const encTitle = encodeURIComponent(title || "");
  const encDek = encodeURIComponent(dek || "");

  const targets = [
    {
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?text=${encTitle}&url=${encUrl}`,
      icon: TwitterLogo,
      testId: `${testIdPrefix}-x`,
    },
    {
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      icon: FacebookLogo,
      testId: `${testIdPrefix}-facebook`,
    },
    {
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      icon: LinkedinLogo,
      testId: `${testIdPrefix}-linkedin`,
    },
    {
      label: "Share by email",
      // mailto: is deliberately last in tab order because it hijacks
      // focus by opening the OS mail handler on click.
      href: `mailto:?subject=${encTitle}&body=${encDek ? encDek + "%0A%0A" : ""}${encUrl}`,
      icon: EnvelopeSimple,
      testId: `${testIdPrefix}-email`,
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press the URL bar to copy manually.");
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid={`${testIdPrefix}-strip`}
      aria-label="Share this post"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B6B6B] mr-1">
        Share
      </span>
      {isNativeApp() ? (
        // Native: one button → iOS/Android system share sheet.
        <button
          type="button"
          onClick={() => { haptic("light"); nativeShare({ title, text: dek, url }); }}
          aria-label="Share"
          data-testid={`${testIdPrefix}-native`}
          className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full border border-[#EAEAEA] bg-white text-[11px] font-bold uppercase tracking-[0.15em] text-ink hover:bg-[#FBF7EC] hover:border-[#C9A227]/50 transition-colors"
        >
          <ShareNetwork size={13} weight="bold" />
          Share
        </button>
      ) : (
      targets.map((t) => (
        <a
          key={t.testId}
          href={t.href}
          target={t.href.startsWith("mailto:") ? undefined : "_blank"}
          rel={t.href.startsWith("mailto:") ? undefined : "noopener noreferrer"}
          aria-label={t.label}
          title={t.label}
          data-testid={t.testId}
          className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#EAEAEA] bg-white text-[#6B6B6B] hover:bg-[#FBF7EC] hover:text-ink hover:border-[#C9A227]/50 transition-colors"
        >
          <t.icon size={14} weight="bold" />
        </a>
      ))
      )}
      <button
        type="button"
        onClick={copy}
        aria-label="Copy link"
        title="Copy link"
        data-testid={`${testIdPrefix}-copy`}
        className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full border border-[#EAEAEA] bg-white text-[11px] font-bold uppercase tracking-[0.15em] text-ink hover:bg-[#FBF7EC] hover:border-[#C9A227]/50 transition-colors"
      >
        {copied ? <Check size={12} weight="bold" /> : <LinkIcon size={12} weight="bold" />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}
